import produce from 'immer';
import cloneDeep from 'lodash/cloneDeep';
import uniq from 'lodash/uniq';

import { createReducer } from 'utils/redux';

import {
  SelectStorySnippetsAction,
  SelectStorySnippetsState
} from '../types';

const EMPTY_STATE: SelectStorySnippetsState = {
  dates: [null, null],
  searchBy: [],
  selectedStorySnippets: [],
  storySnippetPreview: null,
  storySnippets: null,
  onSelectCallback: null,
  saveChangesOnClose: true,
  deliverableId: null,
  viewMode: 'grid',
  campaignStartDate: null
};

const initialState = cloneDeep(EMPTY_STATE);

export default createReducer<SelectStorySnippetsState, SelectStorySnippetsAction>(
  'selectStorySnippets',
  initialState,
  {
    'change-date-range': (state, { dates }) => produce(
      state, draft => {
        draft.dates = dates;
        draft.viewMode = 'grid';
        draft.storySnippetPreview = null;
      }),
    'update-search-by': (state, { keywords }) => produce(
      state, draft => {
        draft.searchBy = keywords.slice(keywords.length - 1);
        draft.viewMode = 'grid';
        draft.storySnippetPreview = null;
      }),
    'update-story-snippets': (state, { snippets }) => produce(
      state, draft => {
        draft.storySnippets = snippets;
        draft.selectedStorySnippets = uniq([
          ...snippets.filter(s => s.selected).map(s => s.code),
          ...draft.selectedStorySnippets
        ]);
      }
    ),
    'toggle-story-snippet': (state, { snippetId }) => produce(
      state, draft => {
        if (draft.selectedStorySnippets.includes(snippetId)) {
          draft.selectedStorySnippets = draft.selectedStorySnippets.filter(s => s !== snippetId);
        } else {
          draft.selectedStorySnippets.push(snippetId);
        }
      }
    ),
    'change-story-snippet-preview': (state, { snippetId }) => produce(
      state, draft => {
        if (draft.storySnippetPreview === snippetId) {
          draft.storySnippetPreview = null;
          draft.viewMode = 'grid';
        } else {
          draft.storySnippetPreview = snippetId;
          draft.viewMode = 'preview-grid';
        }
      }
    ),
    'update-on-select-callback': (state, { cb }) => produce(
      state, draft => {
        draft.onSelectCallback = cb;
      }
    ),
    'update-save-on-select': (state, { saveChangesOnClose }) => produce(
      state, draft => {
        draft.saveChangesOnClose = saveChangesOnClose;
      }
    ),
    'set-deliverable-id': (state, { deliverableId }) => produce(
      state, draft => {
        draft.deliverableId = deliverableId;
      }
    ),
    'flush-state': state => produce(
      state, draft => {
        const nextState = cloneDeep(EMPTY_STATE);
        const keys = Object.keys(nextState);

        keys.forEach(key => {
          draft[key] = nextState[key];
        });
      }
    ),
    'change-view-mode': (state, { viewMode }) => produce(
      state, draft => {
        draft.viewMode = viewMode;

        if (viewMode === 'preview-grid') {
          draft.storySnippetPreview = (draft.storySnippets ?? [])[0]?.code ?? null;
        } else {
          draft.storySnippetPreview = null;
        }
      }
    ),
    'set-initial-selected-story-snippets': (state, { selectedStorySnippets }) => produce(
      state, draft => {
        draft.selectedStorySnippets = selectedStorySnippets;
      }
    ),
    'set-campaign-start-date': (state, { campaignStartDate }) => produce(
      state, draft => {
        draft.campaignStartDate = campaignStartDate;
      }
    )
  }
);
